/**
 * The presenter photo library, opened from any board's thumbnail panel
 * (F-134) — where every editor sees the shared photos, adds to them, and
 * reads what the AI made of each. Admins get their two corrections here as
 * well; nobody gets a delete, by the owner's rule (the library grows only).
 *
 * A dialog rather than a page: the panel is where the question "which faces
 * will my thumbnails use?" comes up, and the answer must not cost a trip to
 * /admin — which most editors cannot open anyway.
 */
import { isPresenterPhotoOffered, PRESENTER_PHOTO_MAX_COUNT } from "@kola/shared";

import { useMe } from "../../core/useMe.js";
import { Modal } from "../../ui/Modal.js";
import { PhotoCard } from "./PhotoCard.js";
import { usePresenterPhotos, usePresenterPhotosLoaded } from "./store.js";
import { useLibraryUploads, type LibraryUploads } from "./uploads.js";

/** The "Add photos" control: a styled label around a hidden multi-file input. */
export function AddPhotosButton({
  uploads,
  atCap,
  className = "btn btn-primary",
  compact = false,
}: {
  uploads: LibraryUploads;
  atCap: boolean;
  className?: string;
  /** Short copy for a tight spot (the thumbnail panel's presenter card). */
  compact?: boolean;
}) {
  const { adding } = uploads;
  return (
    <label className={`${className} ppl-add${adding !== null ? " busy" : ""}`}>
      {adding !== null
        ? compact
          ? `${adding.done}/${adding.total}…`
          : `Adding ${adding.done} of ${adding.total}…`
        : "Add photos"}
      <input
        type="file"
        accept="image/*"
        multiple
        hidden
        disabled={adding !== null || atCap}
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = ""; // let the same files be picked again
          void uploads.addFiles(files);
        }}
      />
    </label>
  );
}

/** The library's grid with its toolbar — the dialog's body, and /admin's section body. */
export function PresenterLibrary({ uploads, editable }: { uploads: LibraryUploads; editable: boolean }) {
  const photos = usePresenterPhotos();
  const loaded = usePresenterPhotosLoaded();
  const untagged = photos.filter((photo) => photo.tags === undefined);
  const offered = photos.filter(isPresenterPhotoOffered).length;
  const atCap = photos.length >= PRESENTER_PHOTO_MAX_COUNT;
  const anyTagging = Object.keys(uploads.tagging).length > 0;

  return (
    <>
      <div className="ppl-toolbar">
        <AddPhotosButton uploads={uploads} atCap={atCap} />
        {untagged.length > 0 && (
          <button
            type="button"
            className="btn"
            disabled={anyTagging}
            onClick={() => {
              for (const photo of untagged) void uploads.retryTag(photo.id);
            }}
          >
            Describe {untagged.length} undescribed
          </button>
        )}
        <span className="ppl-count">
          {photos.length} {photos.length === 1 ? "photo" : "photos"} · {offered} offered to runs
        </span>
      </div>

      {atCap && (
        <p className="tx-hint">The library holds {PRESENTER_PHOTO_MAX_COUNT} photos — it is full.</p>
      )}

      {!loaded ? (
        <p className="admin-card-hint ppl-empty">Loading…</p>
      ) : photos.length === 0 ? (
        <p className="admin-card-hint ppl-empty">
          No photos yet. Add a dozen or more with different expressions — laughing, surprised,
          skeptical, pointing, thinking — and every thumbnail gets the face that fits its hook.
        </p>
      ) : (
        <ul className="ppl-grid">
          {photos.map((photo) => (
            <PhotoCard
              key={photo.id}
              photo={photo}
              editable={editable}
              tagging={uploads.tagging[photo.id] === true}
              error={uploads.errors[photo.id] ?? null}
              onRetryTag={() => void uploads.retryTag(photo.id)}
            />
          ))}
        </ul>
      )}
    </>
  );
}

export function PresenterLibraryDialog({ onClose }: { onClose: () => void }) {
  const { me } = useMe();
  const uploads = useLibraryUploads();
  return (
    <Modal title="Presenter photos" onClose={onClose} className="modal-wide">
      <p className="admin-card-hint ppl-dialog-hint">
        Shared with everyone. Each photo is described by AI as it lands — its expression, pose
        and gaze — and every thumbnail run picks the photo whose expression fits its hook, and
        keeps that expression exactly. Photos stay in the library; an admin can keep one out of
        runs.
      </p>
      <PresenterLibrary uploads={uploads} editable={me?.role === "admin"} />
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}
